import { Litigation } from '@/Litigation/litigation.entity';
import { useCapabilityStore } from '@/stores/capability';
import { useMemberStore } from '@/stores/member';

import { Analysis, AnalysisErrorStatuses, AnalysisStatus } from '../Analysis/analysis.entity';
import { Member, MemberRole } from '../Member/member.entity';
import { MemberCapability } from './member-capability';

const hasCapability = (capability: MemberCapability): boolean => useCapabilityStore().hasCapability(capability);

const currentMember = (): Member | null => useMemberStore().member;

const inLitigation = (litigation: Litigation): boolean =>
    (currentMember()?.litigations ?? []).some((l) => l.litigationId === litigation.litigationId);

// -----------------------------------------------------------------------------
// Analysis
// -----------------------------------------------------------------------------

export const canCheckAnalysis = (analysis: Analysis): boolean => {
    const hasErrors = (analysis.errors ?? []).some((e) => e.status === AnalysisErrorStatuses.UNPROCESSED);
    const fromValidStatus = [AnalysisStatus.NOT_STARTED, AnalysisStatus.ONGOING].includes(analysis.status);
    const recheckWithErrors = analysis.status === AnalysisStatus.CHECKING && hasErrors;

    if (hasCapability(MemberCapability.ANALYSIS_CHECK_ANY)) return fromValidStatus || recheckWithErrors;
    if (hasCapability(MemberCapability.ANALYSIS_CHECK_OWN)) {
        return analysis.owner.memberId === currentMember()?.memberId && (fromValidStatus || recheckWithErrors);
    }
    return false;
};

export const canFlagAnalysisForReview = (analysis: Analysis): boolean => {
    const hasErrors = (analysis.errors ?? []).some((e) => e.status === AnalysisErrorStatuses.UNPROCESSED);
    return (
        hasCapability(MemberCapability.ANALYSIS_FLAG_FOR_REVIEW) &&
        analysis.owner.memberId === currentMember()?.memberId &&
        analysis.status === AnalysisStatus.CHECKING &&
        !hasErrors
    );
};

export const canApproveAnalysis = (analysis: Analysis): boolean => {
    const hasErrors = (analysis.errors ?? []).some((e) => e.status === AnalysisErrorStatuses.UNPROCESSED);
    const fromValidStatus = [AnalysisStatus.CHECKING, AnalysisStatus.TO_REVIEW].includes(analysis.status);
    if (!fromValidStatus || hasErrors) return false;

    // Prevent the owner from approving their own analysis in TO_REVIEW state
    if (analysis.status === AnalysisStatus.TO_REVIEW && analysis.owner.memberId === currentMember()?.memberId) {
        return false;
    }

    if (hasCapability(MemberCapability.ANALYSIS_APPROVE_ANY)) return true;
    if (hasCapability(MemberCapability.ANALYSIS_APPROVE_OWN_LITIGATION)) return inLitigation(analysis.litigation);
    if (hasCapability(MemberCapability.ANALYSIS_APPROVE_OWN)) {
        return analysis.owner.memberId === currentMember()?.memberId && inLitigation(analysis.litigation);
    }
    return false;
};

export const canBulkApprove = (litigation: Litigation): boolean => {
    if (hasCapability(MemberCapability.ANALYSIS_APPROVE_ANY)) return true;
    if (hasCapability(MemberCapability.ANALYSIS_APPROVE_OWN_LITIGATION)) return inLitigation(litigation);
    if (hasCapability(MemberCapability.ANALYSIS_APPROVE_OWN)) return inLitigation(litigation);
    return false;
};

export const canViewAnalysis = (analysis: Analysis): boolean => {
    if (hasCapability(MemberCapability.ANALYSIS_READ_ANY)) return true;
    if (hasCapability(MemberCapability.ANALYSIS_READ_OWN_LITIGATION)) return inLitigation(analysis.litigation);
    if (hasCapability(MemberCapability.ANALYSIS_READ_OWN)) {
        const viewStatuses = [
            AnalysisStatus.NOT_STARTED,
            AnalysisStatus.ONGOING,
            AnalysisStatus.CHECKING,
            AnalysisStatus.TO_REVIEW,
        ];
        if (hasCapability(MemberCapability.ANALYSIS_READ_OWN_APPROVED)) viewStatuses.push(AnalysisStatus.APPROVED);
        return analysis.owner.memberId === currentMember()?.memberId && viewStatuses.includes(analysis.status);
    }
    return false;
};

export const canEditAnalysis = (analysis: Analysis): boolean => {
    if (hasCapability(MemberCapability.ANALYSIS_EDIT_ANY)) return true;
    if (hasCapability(MemberCapability.ANALYSIS_EDIT_OWN_LITIGATION)) return inLitigation(analysis.litigation);
    if (hasCapability(MemberCapability.ANALYSIS_EDIT_OWN)) {
        const editStatuses = [AnalysisStatus.NOT_STARTED, AnalysisStatus.ONGOING, AnalysisStatus.CHECKING];
        return analysis.owner.memberId === currentMember()?.memberId && editStatuses.includes(analysis.status);
    }
    return false;
};

export const canDeleteAnalysis = (analysis: Analysis): boolean => {
    if (hasCapability(MemberCapability.ANALYSIS_DELETE_ANY)) return true;
    if (hasCapability(MemberCapability.ANALYSIS_DELETE_OWN_LITIGATION)) return inLitigation(analysis.litigation);
    if (hasCapability(MemberCapability.ANALYSIS_DELETE_OWN)) {
        const deleteStatuses = [
            AnalysisStatus.NOT_STARTED,
            AnalysisStatus.ONGOING,
            AnalysisStatus.CHECKING,
            AnalysisStatus.TO_REVIEW,
        ];
        if (hasCapability(MemberCapability.ANALYSIS_DELETE_OWN_APPROVED)) deleteStatuses.push(AnalysisStatus.APPROVED);
        return analysis.owner.memberId === currentMember()?.memberId && deleteStatuses.includes(analysis.status);
    }
    return false;
};

export const canAssignAnalyst = (litigation: Litigation): boolean => {
    if (hasCapability(MemberCapability.ANALYSIS_ASSIGN_ANY)) return true;
    if (hasCapability(MemberCapability.ANALYSIS_ASSIGN_OWN_LITIGATION)) return inLitigation(litigation);

    return false;
};

// -----------------------------------------------------------------------------
// Litigation
// -----------------------------------------------------------------------------

export const canCreateLitigation = (): boolean => hasCapability(MemberCapability.LITIGATION_CREATE);

export const canEditLitigation = (litigation: Litigation): boolean =>
    hasCapability(MemberCapability.LITIGATION_EDIT_ANY) ||
    (hasCapability(MemberCapability.LITIGATION_EDIT_OWN) && inLitigation(litigation));

export const canExportLitigation = (litigation: Litigation): boolean =>
    hasCapability(MemberCapability.LITIGATION_EXPORT_ANY) ||
    (hasCapability(MemberCapability.LITIGATION_EXPORT_OWN) && inLitigation(litigation));

// -----------------------------------------------------------------------------
// Member
// -----------------------------------------------------------------------------

export const canCreateMember = (): boolean => hasCapability(MemberCapability.MEMBER_CREATE);
export const canChangePassword = (): boolean => hasCapability(MemberCapability.MEMBER_EDIT_OWN_PASSWORD);
export const canAssignMemberRole = (subject: Member, targetRole: MemberRole): boolean => {
    const isSelf = currentMember()?.memberId === subject.memberId;
    const isUserLevel = [MemberRole.USER, MemberRole.EXPERT].includes(targetRole);

    if (isUserLevel) {
        return (
            hasCapability(MemberCapability.MEMBER_EDIT_ANY) ||
            hasCapability(MemberCapability.MEMBER_EDIT_USER_LEVEL) ||
            (isSelf && hasCapability(MemberCapability.MEMBER_EDIT_OWN_PROFILE))
        );
    }

    return (
        hasCapability(MemberCapability.MEMBER_EDIT_ANY) ||
        (isSelf && hasCapability(MemberCapability.MEMBER_EDIT_OWN_PROFILE))
    );
};

export const canEditMember = (subject: Member): boolean => {
    if (!subject.role) return false;
    return canAssignMemberRole(subject, subject.role as MemberRole);
};

export const canViewMemberDetails = (subject: Member): boolean => {
    return canEditMember(subject);
};

export const canRequirePasswordReset = (subject: Pick<Member, 'memberId'>): boolean => {
    const isSelf = currentMember()?.memberId === subject.memberId;
    return !isSelf && hasCapability(MemberCapability.MEMBER_REQUIRE_PASSWORD_RESET);
};
